import { CopyableLink, Label, LabelledField, Spinner, Wizard, type WizardStep } from "@mixitone/components";
import { ApplicationView } from "@mixitone/mvc";
import type { FormFieldConfig } from "components/Forms/FormFieldsDefinition";
import { ValueComponent } from "components/Forms/FormValueComponents";
import PaymentMethodsSection from "components/Forms/PaymentMethodsSection";
import TournamentPaymentMethodForm from "components/Forms/TournamentPaymentMethodForm";
import { TournamentSessionFormController } from "components/Forms/TournamentSessionForm";
import WizardModal from "components/common/WizardModal";
import { injectModal } from "lib/injectParams";
import { useMemo } from "react";
import { useModalParams } from "router";

function resolveInitialStep(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value !== "") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return 0;
}

const ControlledSignupsWizard: React.FC = () => {
  const controller = TournamentSessionFormController.use();
  const modalParams = useModalParams() as { id: string; initialStep?: unknown };
  const initialStep = resolveInitialStep(modalParams.initialStep);
  const {
    loading,
    record,
    paymentMethods,
    showPaymentMethodModal,
    selectedPaymentType,
    selectedPaymentMethod,
    selectedPaymentMethodId,
  } = controller.state;
  const fields = useMemo(() => controller.fields(), []);

  // Organize fields into steps
  const stepFields = {
    description: [fields.banner_image_id, fields.description],
    payment: [fields.payment_by_player_or_entry],
    confirmation: [fields.confirmation_message],
    visibility: [fields.is_public],
  };

  if (loading) return null;

  const registrationLink = record.registrationLink(import.meta.env.VITE_PORTAL_URL);
  const shortLink = record.shortRegistrationLink(import.meta.env.VITE_SITE_URL || window.location.origin);

  const getValueComponent = (field: FormFieldConfig, i: number) => (
    <LabelledField key={i} label={field.label} helpText={field.description} htmlFor={field.name}>
      {field.type === "checkbox" ? (
        <ValueComponent field={field} record={record} autoFocus={i === 0} variant="toggle" />
      ) : (
        <ValueComponent field={field} record={record} autoFocus={i === 0} />
      )}
    </LabelledField>
  );

  const validateFields = (fields: FormFieldConfig[]) => {
    return () =>
      fields.every((field) => {
        if (!field.required) return true;
        const value = (record as unknown as Record<string, unknown>)[field.name];
        return !!value;
      });
  };

  const steps: WizardStep[] = [
    {
      name: "Description",
      title: "Tournament description",
      subtitle: "Provide details about your tournament",
      content: <>{stepFields.description.map(getValueComponent)}</>,
      validate: validateFields(stepFields.description),
    },
    {
      name: "Payment",
      title: "Payment methods",
      subtitle: "Configure how players can pay for entry",
      content: (
        <div className="space-y-8">
          <PaymentMethodsSection paymentMethods={paymentMethods} />
          {stepFields.payment.map(getValueComponent)}
        </div>
      ),
      validate: () => true, // Payment methods are optional
    },
    {
      name: "Confirmation",
      title: "Confirmation message",
      subtitle: "Customize the message players see after you confirm their entry",
      content: <>{stepFields.confirmation.map(getValueComponent)}</>,
      validate: validateFields(stepFields.confirmation),
    },
    {
      name: "Enabled",
      title: "Turn on signups",
      subtitle: "Control whether players can sign up for this tournament",
      content: (
        <div className="grid grid-cols-[min-content_1fr] gap-4">
          <Label className="whitespace-nowrap">Enable signups</Label>
          <ValueComponent field={stepFields.visibility[0]} record={record} autoFocus variant="toggle" />
          {shortLink ? (
            <>
              <Label className="whitespace-nowrap">Short link</Label>
              <CopyableLink url={shortLink} copyLabel="Copy" />
              <Label className="whitespace-nowrap">Full link</Label>
              {registrationLink ? (
                <CopyableLink url={registrationLink} copyLabel="Copy" />
              ) : (
                <Spinner size={24} />
              )}
            </>
          ) : (
            <>
              <Label className="whitespace-nowrap">Registration link</Label>
              {registrationLink ? (
                <CopyableLink url={registrationLink} copyLabel="Copy" />
              ) : (
                <Spinner size={24} />
              )}
            </>
          )}
        </div>
      ),
      validate: validateFields(stepFields.visibility),
    },
  ];
  return (
    <>
      <WizardModal>
        <Wizard
          steps={steps}
          initialStep={initialStep}
          onComplete={() => controller.actionSubmit()}
          doneButtonText="Save configuration"
        />
      </WizardModal>
      {showPaymentMethodModal && selectedPaymentType && (
        <TournamentPaymentMethodForm
          tournament_session_id={controller.state.record.id!}
          payment_type={selectedPaymentType}
          id={selectedPaymentMethodId || undefined}
          onClose={controller.actionHidePaymentMethodModal.bind(controller)}
          onSave={controller.actionSavePaymentMethod.bind(controller)}
          attributes={{
            tournament_session_id: controller.state.record.id!,
            payment_type: selectedPaymentType,
          }}
          paymentMethod={selectedPaymentMethod ?? undefined}
        />
      )}
    </>
  );
};

const SignupsWizard = TournamentSessionFormController.scope(ApplicationView(ControlledSignupsWizard));

const EditTournamentModal: React.FC<{ id: string }> = ({ id }) => {
  return <SignupsWizard attributes={{}} id={id} />;
};

export default injectModal(ApplicationView(EditTournamentModal));
